import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clock3,
  Eye,
  Play,
  RefreshCcw,
  Sparkles,
  WandSparkles,
} from 'lucide-react'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Modal } from '../../components/Modal'
import { useDemoStore } from '../../store'
import type { WorkflowItem, WorkflowRunItem } from '../../types'
import { DemoNotice, PageTitle, StatusBadge } from './shared'

const workflowKeywords: Record<string, string[]> = {
  'di-founder-command-brief': ['priority', 'priorities', 'brief', 'plan', 'decision', 'founder', 'business'],
  'di-stack-health-watch': ['system', 'stack', 'health', 'failure', 'failed', 'job', 'downtime', 'service'],
  'di-business-brain-sync': ['brand', 'brain', 'memory', 'source', 'rule', 'knowledge', 'document'],
  'di-customer-enquiry-triage': ['customer', 'support', 'sales', 'enquiry', 'email', 'reply', 'response'],
  'di-fortnightly-newsletter': ['newsletter', 'email', 'subscriber', 'audience', 'mailing'],
  'di-monthly-content-system': ['content', 'social', 'post', 'calendar', 'instagram', 'tiktok', 'blog'],
  'di-campaign-concept-to-approval': ['campaign', 'advert', 'advertising', 'ad', 'launch', 'promotion'],
  'di-market-pain-point-watch': ['research', 'market', 'competitor', 'reddit', 'pain point', 'trend', 'opportunity'],
  'di-product-readiness-qa': ['product', 'ready', 'qa', 'quality', 'pricing', 'package', 'release'],
  'di-digital-product-build': ['workbook', 'guide', 'course', 'journal', 'lead magnet', 'digital product'],
  'di-asset-provenance-qa': ['image', 'asset', 'creative', 'logo', 'mockup', 'banner', 'design'],
  'di-website-conversion-audit': ['website', 'site', 'seo', 'conversion', 'homepage', 'page', 'traffic'],
  'di-product-catalogue-integrity': ['catalogue', 'collection', 'link', 'product feed', 'storefront'],
  'di-weekly-case-study-evidence': ['case study', 'evidence', 'proof', 'metric', 'time saved', 'result'],
  'di-monthly-operating-review': ['review', 'performance', 'operating', 'monthly', 'scorecard'],
}

function recommendWorkflow(objective: string, workflows: WorkflowItem[]): WorkflowItem | null {
  const normalised = objective.toLowerCase()
  let best: { workflow: WorkflowItem; score: number } | null = null

  for (const workflow of workflows) {
    if (workflow.status !== 'ready') continue
    const terms = workflowKeywords[workflow.id] ?? []
    const score = terms.reduce((total, term) => total + (normalised.includes(term) ? term.length : 0), 0)
    if (!best || score > best.score) best = { workflow, score }
  }

  return best?.score ? best.workflow : workflows.find(item => item.status === 'ready') ?? null
}

function statusTone(status: WorkflowRunItem['status']): 'good' | 'warn' | 'danger' | 'neutral' {
  if (status === 'completed') return 'good'
  if (status === 'failed' || status === 'cancelled') return 'danger'
  if (status === 'blocked' || status === 'awaiting-approval') return 'warn'
  return 'neutral'
}

function formatDuration(milliseconds: number | null): string {
  if (milliseconds === null) return '—'
  const seconds = Math.max(0, Math.round(milliseconds / 1000))
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60
  return minutes ? `${minutes}m ${remainder}s` : `${remainder}s`
}

function formatTokens(run: WorkflowRunItem): string {
  const total = run.tokenUsage?.totalTokens
  return typeof total === 'number' ? total.toLocaleString('en-GB') : '—'
}

export function TasksPage() {
  const {
    workflows,
    runs,
    apiMode,
    apiError,
    hermesState,
    launchWorkflow,
    refreshLiveData,
  } = useDemoStore()
  const readyWorkflows = useMemo(() => workflows.filter(item => item.status === 'ready'), [workflows])
  const hasActiveRuns = runs.some(run => ['queued', 'running', 'awaiting-approval'].includes(run.status))
  const [objective, setObjective] = useState('')
  const [desiredOutcome, setDesiredOutcome] = useState('')
  const [priority, setPriority] = useState<'low' | 'normal' | 'high' | 'urgent'>('normal')
  const [targetDate, setTargetDate] = useState('')
  const [notes, setNotes] = useState('')
  const [selectedWorkflow, setSelectedWorkflow] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submittedRun, setSubmittedRun] = useState<WorkflowRunItem | null>(null)
  const [selectedRun, setSelectedRun] = useState<WorkflowRunItem | null>(null)

  useEffect(() => {
    if (!readyWorkflows.length) return
    if (!readyWorkflows.some(item => item.id === selectedWorkflow)) {
      setSelectedWorkflow(readyWorkflows[0].id)
    }
  }, [readyWorkflows, selectedWorkflow])

  useEffect(() => {
    if (apiMode !== 'live' || !hasActiveRuns) return
    const interval = window.setInterval(() => void refreshLiveData(), 5_000)
    return () => window.clearInterval(interval)
  }, [apiMode, hasActiveRuns, refreshLiveData])

  const applyRecommendation = () => {
    const recommendation = recommendWorkflow(objective, workflows)
    if (recommendation) setSelectedWorkflow(recommendation.id)
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setSubmittedRun(null)

    if (!objective.trim()) {
      setError('Describe the task you want DAWN to handle.')
      return
    }
    if (!selectedWorkflow) {
      setError('Select an enabled workflow.')
      return
    }
    if (apiMode !== 'live') {
      setError('The local Worker is not connected. Start it on port 8787, then retry.')
      return
    }
    if (!hermesState.reachable) {
      setError('Hermes is not connected to the DAWN Worker. Complete the local Hermes API setup, then refresh.')
      return
    }

    setSubmitting(true)
    try {
      const run = await launchWorkflow(selectedWorkflow, {
        objective: objective.trim(),
        desiredOutcome: desiredOutcome.trim() || undefined,
        priority,
        targetDate: targetDate || undefined,
        notes: notes.trim() || undefined,
        owner: 'Anthon Linton',
        approvalPolicy: 'Tenant 001 governed',
        submittedFrom: 'dawn-task-inbox',
      }, 30)
      setSubmittedRun(run)
      setObjective('')
      setDesiredOutcome('')
      setNotes('')
      setTargetDate('')
      setPriority('normal')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The task could not be queued.')
    } finally {
      setSubmitting(false)
    }
  }

  const liveReady = apiMode === 'live' && hermesState.reachable

  return <>
    <PageTitle
      title="Task Inbox"
      copy="Give DAWN work in plain language. Hermes executes it inside the Tenant 001 policy and returns evidence for approval."
      actions={<button className="button button--outline" onClick={() => void refreshLiveData()}><RefreshCcw size={16} /> Refresh</button>}
    />
    <DemoNotice />

    {submittedRun && <div className="success-banner"><CheckCircle2 size={17} /><span><strong>Task submitted to DAWN.</strong> Run {submittedRun.id} is {submittedRun.status.replace('-', ' ')}.</span></div>}
    {(error || apiError) && <div className="success-banner"><AlertTriangle size={17} /><span><strong>Attention required.</strong> {error || apiError}</span></div>}

    <div className="dashboard-grid">
      <section className="app-panel app-panel--wide">
        <div className="panel-heading">
          <div><h2>Give DAWN a task</h2><p>Start with the outcome. The brief, sources, execution and decisions become case-study evidence.</p></div>
          <div className="runtime-badges">
            <StatusBadge tone={apiMode === 'live' ? 'good' : 'warn'}>{apiMode === 'live' ? 'D1 connected' : 'Worker required'}</StatusBadge>
            <StatusBadge tone={hermesState.reachable ? 'good' : 'warn'}>{hermesState.reachable ? 'Hermes connected' : 'Hermes setup required'}</StatusBadge>
          </div>
        </div>
        <form className="modal-form" onSubmit={submit}>
          <label>What do you want done?<textarea rows={5} value={objective} onChange={event => setObjective(event.target.value)} placeholder="Example: Review every digital product currently in the production queue, identify anything blocking release, and prepare a prioritised action list with evidence." /></label>
          <div className="detail-grid">
            <label>Desired outcome<input value={desiredOutcome} onChange={event => setDesiredOutcome(event.target.value)} placeholder="A decision-ready report, draft, asset pack…" /></label>
            <label>Priority<select value={priority} onChange={event => setPriority(event.target.value as typeof priority)}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></label>
            <label>Target date<input type="date" value={targetDate} onChange={event => setTargetDate(event.target.value)} /></label>
            <label>Workflow<select value={selectedWorkflow} onChange={event => setSelectedWorkflow(event.target.value)}>{workflows.map(item => <option key={item.id} value={item.id} disabled={item.status !== 'ready'}>{item.name}{item.status !== 'ready' ? ' · Stage 2 disabled' : ''}</option>)}</select></label>
          </div>
          <label>Additional context<textarea rows={3} value={notes} onChange={event => setNotes(event.target.value)} placeholder="Constraints, sources to use, quality requirements, what must not happen…" /></label>
          <div className="modal-actions">
            <button type="button" className="button button--outline" onClick={applyRecommendation} disabled={!objective.trim()}><WandSparkles size={16} /> Recommend workflow</button>
            <button className="button button--gold" disabled={submitting || !liveReady}><Play size={16} /> {submitting ? 'Submitting task…' : 'Start governed task'}</button>
          </div>
        </form>
      </section>

      <section className="app-panel">
        <div className="panel-heading"><div><h2>Live execution path</h2><p>The brand can operate with less guidance while consequential actions remain controlled.</p></div><Bot size={18} /></div>
        <ol className="step-list">
          <li><span>1</span><div><strong>DAWN governs the brief</strong><p>The workflow, priority, source scope and non-negotiable restrictions are recorded in D1.</p></div></li>
          <li><span>2</span><div><strong>Hermes performs the work</strong><p>It researches, reads approved sources, validates and prepares the requested result.</p></div></li>
          <li><span>3</span><div><strong>Permissions pause in DAWN</strong><p>Any Hermes tool request waits in Approvals and resumes only after your one-action decision.</p></div></li>
          <li><span>4</span><div><strong>The result returns for acceptance</strong><p>Output, evidence, timings, token usage and blockers are retained for the live case study.</p></div></li>
        </ol>
      </section>
    </div>

    <section className="app-panel executions-panel">
      <div className="panel-heading"><div><h2>Live task history</h2><p>DAWN runs, Hermes progress, approvals and accepted results.</p></div></div>
      <div className="table-wrap"><table><thead><tr><th>Run</th><th>Workflow</th><th>Status</th><th>Hermes</th><th>Created</th><th>Duration</th><th>Tokens</th><th>Result</th></tr></thead><tbody>
        {runs.length ? runs.slice(0, 20).map(run => <tr key={run.id}><td>{run.id.slice(0, 14)}…</td><td>{run.workflowName}</td><td><StatusBadge tone={statusTone(run.status)}>{run.status.replace('-', ' ')}</StatusBadge></td><td>{run.agentStatus || 'pending'}</td><td>{new Date(run.createdAt).toLocaleString('en-GB')}</td><td><Clock3 size={14} /> {formatDuration(run.durationMs)}</td><td>{formatTokens(run)}</td><td><button className="button button--outline button--small" onClick={() => setSelectedRun(run)}><Eye size={15} /> View</button></td></tr>) : <tr><td colSpan={8}>No live task runs yet. Connect Hermes and submit the first task above.</td></tr>}
      </tbody></table></div>
    </section>

    <Modal open={Boolean(selectedRun)} onClose={() => setSelectedRun(null)} title={selectedRun?.workflowName || 'Task result'}>
      {selectedRun && <div className="workflow-detail">
        <div className="detail-grid">
          <div><small>DAWN status</small><strong>{selectedRun.status.replace('-', ' ')}</strong></div>
          <div><small>Hermes status</small><strong>{selectedRun.agentStatus || 'Not started'}</strong></div>
          <div><small>Hermes run</small><strong>{selectedRun.agentRunId || '—'}</strong></div>
          <div><small>Approval pauses</small><strong>{selectedRun.approvalCount || 0}</strong></div>
          <div><small>Duration</small><strong>{formatDuration(selectedRun.durationMs)}</strong></div>
          <div><small>Tokens</small><strong>{formatTokens(selectedRun)}</strong></div>
        </div>
        {selectedRun.agentLastEvent && <article className="proposal-preview"><div className="proposal-preview__bar"><Sparkles size={16} />Latest Hermes event</div><p>{selectedRun.agentLastEvent}</p></article>}
        {selectedRun.outputText ? <pre className="run-output">{selectedRun.outputText}</pre> : selectedRun.errorMessage ? <article className="proposal-preview"><div className="proposal-preview__bar"><AlertTriangle size={16} />Execution issue</div><h3>{selectedRun.errorCode || 'Task issue'}</h3><p>{selectedRun.errorMessage}</p></article> : <article className="proposal-preview"><div className="proposal-preview__bar"><Clock3 size={16} />Task in progress</div><p>Hermes has not returned a final report yet. DAWN refreshes this view while the run is active.</p></article>}
        <div className="modal-actions"><button className="button button--outline" onClick={() => setSelectedRun(null)}>Close</button></div>
      </div>}
    </Modal>
  </>
}
